import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";

export type ShiftLogFormProps = {
  api: string;
  token?: string;
  moId: number;
  moName: string;
  onSubmitted?: () => void;
};

type Shift = { id: number; name: string };
type Brand = { id: number; name: string };

const ShiftLogForm: React.FC<ShiftLogFormProps> = ({ api, token, moId, moName, onSubmitted }) => {
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [shifts, setShifts] = useState<Shift[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(true);

  const [shiftId, setShiftId] = useState<number | "">("");
  const [brandId, setBrandId] = useState<number | "">("");
  const [aRolls, setARolls] = useState("");
  const [bRolls, setBRolls] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: "success" | "error" }>
    ({ open: false, msg: "", severity: "success" });

  useEffect(() => {
    (async () => {
      try {
        const [sRes, bRes] = await Promise.all([
          fetch(`${api}/settings/shifts`, { headers }),
          fetch(`${api}/brands`, { headers }),
        ]);
        const sData = await sRes.json();
        const bData = await bRes.json();
        setShifts(sData.shifts ?? []);
        setBrands((bData as Brand[]).filter((b: any) => b.is_active));
      } catch {
        // silently continue; dropdowns will be empty
      } finally {
        setLoadingMeta(false);
      }
    })();
  }, []); // eslint-disable-line

  const aNum = parseInt(aRolls) || 0;
  const bNum = parseInt(bRolls) || 0;
  const canSubmit = (aNum > 0 || bNum > 0) && aNum >= 0 && bNum >= 0 && brandId !== "";

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const selectedShift = shifts.find(s => s.id === shiftId);
      const selectedBrand = brands.find(b => b.id === brandId);
      const res = await fetch(`${api}/mos/${moId}/logs`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          shift_id: shiftId || null,
          shift_name: selectedShift?.name ?? null,
          brand_id: brandId || null,
          brand_name: selectedBrand?.name ?? null,
          a_roll_count: aNum,
          b_roll_count: bNum,
          notes: notes.trim() || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Submit failed");
      }
      setShiftId("");
      setBrandId("");
      setARolls("");
      setBRolls("");
      setNotes("");
      setSnack({ open: true, msg: "Entry submitted", severity: "success" });
      onSubmitted?.();
    } catch (e: any) {
      setSnack({ open: true, msg: e.message || "Submit failed", severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingMeta) {
    return <Box display="flex" justifyContent="center" py={3}><CircularProgress size={24} /></Box>;
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.75, sm: 2.5 },
        borderRadius: 2,
        maxWidth: { xs: "100%", sm: 480 },
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} mb={2}>
        Submit Entry — {moName}
      </Typography>

      <Box display="flex" flexDirection="column" gap={2}>
        <TextField
          select
          label="Shift"
          value={shiftId}
          onChange={e => setShiftId(Number(e.target.value))}
          fullWidth
        >
          <MenuItem value="">— Select shift —</MenuItem>
          {shifts.map(s => (
            <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Brand *"
          value={brandId}
          onChange={e => setBrandId(Number(e.target.value))}
          fullWidth
          required
        >
          <MenuItem value="">— Select brand —</MenuItem>
          {brands.map(b => (
            <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
          ))}
        </TextField>

        <Box display="flex" gap={2}>
          <TextField
            label="A Rolls"
            type="number"
            value={aRolls}
            onChange={e => setARolls(e.target.value)}
            inputProps={{ min: 0 }}
            fullWidth
          />
          <TextField
            label="B Rolls"
            type="number"
            value={bRolls}
            onChange={e => setBRolls(e.target.value)}
            inputProps={{ min: 0 }}
            fullWidth
          />
        </Box>

        <TextField
          label="Notes"
          multiline
          rows={2}
          value={notes}
          onChange={e => setNotes(e.target.value)}
          fullWidth
        />

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !canSubmit}
          sx={{
            alignSelf: { xs: "stretch", sm: "flex-start" },
            py: { xs: 1.1, sm: 0.75 },
          }}
        >
          {submitting ? <CircularProgress size={20} /> : "Submit"}
        </Button>
      </Box>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Paper>
  );
};

export default ShiftLogForm;
